import React from "react";
import VisitCard from "./VisitCards";
import { useSubject } from "../context/SubjectContext";
import { useNavigate } from "react-router-dom";
import { getAcademicYear, getUniqueAcademicYears } from "../utils/getYear";
import "./VisitOverview.css";
import {
  windowLabelsByIndex,
  useModerationWindowSession,
} from "../constants/Constants";
import MarksPanelRight from "./MarksPanelRight";
import { getCenterType, getRegistrationKeys } from "../utils/utils";
import { useCenter } from "../context/CenterContext";
import { useConsortiaCenter } from "../context/ConsortiaCenterContext";

const VisitOverview = () => {
  const moderationWindowSession = useModerationWindowSession();
  const { subjectData } = useSubject();
  const navigate = useNavigate();
  const { centerNumber } = useCenter();
  const {selectedSubject, selectedSubjectId} = subjectData || {};
    const { consortiaCentres =[] } = useConsortiaCenter();
          const currentCentreType = getCenterType(consortiaCentres, Number(centerNumber), selectedSubject, selectedSubjectId) || '';
  

  const formatDayMonth = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "long",
    }).format(date);
  };

  const formatDayMonthYear = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date);
  };

  const handleNewVisitClick = () => {
  if (subjectData?.selectedSubject) {
    const routeName = subjectData.selectedSubject
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+/, "")
      .replace(/_+$/, "");
    navigate(`/dashboard/visit_form/${routeName}`, {
          state: { },
        });
  }
};


  const handleViewReportsClick = () => {
    if (!subjectData?.selectedSubject) return;
    const subjectSlug = subjectData.selectedSubject
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+/, "")
    .replace(/_+$/, "");
    navigate(`/dashboard/reports/${subjectSlug}`);
  };

  const seriesList = subjectData?.subjectDetails?.seriesList || [];

  const academicYears = getUniqueAcademicYears(seriesList);
  // Active moderation sessions based on configured session IDs
  // Active moderation sessions based on configured session IDs
  const activeSessionIds = Object.values(moderationWindowSession);
  const activeSessions = seriesList.filter(
    (session) =>
      activeSessionIds.includes(session.sessionId) &&
      session.visitStartDate &&
      session.visitEndDate && session.userEndDate
  );

  // Order active sessions as window1, window2
  const orderedActiveSessions = [
    moderationWindowSession.window1,
    moderationWindowSession.window2,
  ]
    .map((id) => activeSessions.find((s) => s.sessionId === id))
    .filter(Boolean);

  // Header year: for active sessions, base on start year (e.g. 2025 -> 2025/26)
  // Always show the previous academic year for headerYearLabel
let headerYearLabel = "";
if (orderedActiveSessions.length > 0 && orderedActiveSessions[0]?.visitStartDate) {
  const y = new Date(orderedActiveSessions[0].visitStartDate).getFullYear();
  // Show previous: y - 1
  headerYearLabel = `${y - 1}/${String(y).slice(-2)}`;     // e.g. 2026 -> 2025/26
} else if (academicYears[0]) {
  // Fallback: parse first academic year and go to previous
  const [startYear] = academicYears[0].split("/");
  if (startYear) {
    const prevYear = Number(startYear) - 1;
    headerYearLabel = `${prevYear}/${String(startYear).slice(-2)}`;
  } else {
    headerYearLabel = academicYears[0];
  }
}

  const submitAssessmentUrl = process.env.REACT_APP_ASSESSMENT_SUBMIT_URL;
  const handleSubmitEvidenceClick = () => {
    window.open(submitAssessmentUrl, "_blank", "noopener,noreferrer");
  };
  const routeName = subjectData.selectedSubject
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "_")
  .replace(/^_+/, "")
  .replace(/_+$/, "");
  const onSubmitMarks = (type) => {
    const subject = subjectData?.selectedSubject || "subject";
    const subjectSlug = subject
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+/, "")
      .replace(/_+$/, "");

    if (!subjectData?.subjectDetails?.isModerationVisit) {
      navigate(`/dashboard/marks_submission/${subjectSlug}`);
    } else {
      if (type === "submitmarks") {
        navigate(`/dashboard/marks_submission/${subjectSlug}`, {
          state: { mode: "total" },
        });
      } else if (type === "submitAssessmentcriteria") {
        navigate(`/dashboard/marks_submission/${subjectSlug}`, {
          state: { mode: "criteria" },
        });
      }
    }
  };
  const registrationKeys = getRegistrationKeys(currentCentreType, subjectData.subjectDetails);
  const { isRestCandidateRegistered, isConsortiaRestCandidateRegistered } = registrationKeys;
  return (
    <div className="subject-page-content ">
      <div
        className="columns"
        style={{ paddingLeft: "1.5rem", marginRight: "0rem" }}
      >
        <div className="column xis-8 mt-4">
          <div className="card has-background-white-ter">
            <div className="card-head">
              <div className="columns">
                <div className="column">
                  <h2 className="title is-6 mt-2">Moderation visits</h2>
                </div>
                <div className="column is-narrow">
                  <button
                    className="button xis-small is-primary xis-outlined"
                    onClick={handleNewVisitClick}
                    disabled={ currentCentreType  === 'Rest' && (isRestCandidateRegistered === false && isConsortiaRestCandidateRegistered === true) }
                  >
                    <span className="icon mr-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24"
                        viewBox="0 96 960 960"
                        width="24"
                      >
                        <path d="M450.001 845.999v-240h-240v-59.998h240v-240h59.998v240h240v59.998h-240v240h-59.998Z" />
                      </svg>
                    </span>
                    New moderation visit
                  </button>
                </div>
              </div>
            </div>

            {!subjectData?.subjectDetails?.isModerationVisit &&
            seriesList.length ? (
              <div className="card-content">
                <div className="xnotification is-warning is-flex">
                  <span className="icon mr-2">
                    <svg
                      style={{ fill: "#a55900" }}
                      xmlns="http://www.w3.org/2000/svg"
                      height="24px"
                      viewBox="0 -960 960 960"
                      width="24px"
                    >
                      <path d="m40-120 440-760 440 760H40Zm440-120q17 0 28.5-11.5T520-280q0-17-11.5-28.5T480-320q-17 0-28.5 11.5T440-280q0 17 11.5 28.5T480-240Zm-40-120h80v-200h-80v200Z" />
                    </svg>
                  </span>
                  <span>
                    You have not made any moderation visit requests yet.
                  </span>
                </div>

                <div className="box p-4 content mt-5" onClick={() => {}}>
                  <div className="columns is-gapless">
                    <div className="column is-narrow">
                      <span className="icon mr-2">
                        <svg
                          style={{ fill: "#0076BD" }}
                          xmlns="http://www.w3.org/2000/svg"
                          height="24px"
                          viewBox="0 -960 960 960"
                          width="24px"
                        >
                          <path d="M440-280h80v-240h-80v240Zm40-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-680q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-600Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" />
                        </svg>
                      </span>
                    </div>
                    <div className="column">
                      <p>
                        <strong>When can moderation take place?</strong>
                      </p>
                      
                      {/* Dynamically render academic year and visits */}
                      {orderedActiveSessions.length > 0 ? (
                        // If we have active sessions (configured in moderationWindowSession),
                        // show ONLY those as Window 1 / Window 2.
                        <div className="mb-3">
                         
                          <ul>
                            {orderedActiveSessions.map((session, i) => {
                              const windowLabels = windowLabelsByIndex[0] || [];
                              const label =
                                windowLabels[i] || `Window ${i + 1}`;

                              return (
                                <li key={session.sessionId}>
                                  {label}:{" "}
                                  <strong>
                                    {formatDayMonth(session.visitStartDate)} to{" "}
                                    {formatDayMonthYear(session.userEndDate)}
                                  </strong>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : (
                        // Fallback: old behaviour if there are no active sessions
                        academicYears.map((yearLabel, index) => {
                          const visits = seriesList
                            .filter((session) => {
                              const startYear = getAcademicYear(
                                session.visitStartDate
                              );
                              const endYear = getAcademicYear(
                                session.userEndDate
                              );
                              return (
                                startYear === yearLabel || endYear === yearLabel
                              );
                            })
                            .slice(0, 2);

                          const windowLabels = windowLabelsByIndex[index] || [];

                          return (
                            <div className="mb-3" key={`year-${index}`}>
                              <p>
                                <strong>{yearLabel}</strong>
                              </p>
                              <ul>
                                {visits.map((session, i) => (
                                  <li key={session.sessionId}>
                                    {windowLabels[i] || `Window ${i + 1}`}:{" "}
                                    <strong>
                                      {formatDayMonth(session.visitStartDate)}{" "}
                                      to{" "}
                                      {formatDayMonthYear(session.visitEndDate)}
                                    </strong>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <VisitCard />
            )}
          </div>
        </div>
        <div className="column is-5 mt-1 pl-0">
          <MarksPanelRight
            onSubmitEvidence={handleSubmitEvidenceClick}
            onSubmitMarks={onSubmitMarks}
            onViewReports={handleViewReportsClick}
          />
        </div>
      </div>
    </div>
  );
};

export default VisitOverview;
